import { useEffect, useState } from 'react';

import { Link, useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import { KimDeprecationDialog } from './components/kim-deprecation-dialog';
import { ManualAnswerModal } from './components/manual-answer-modal';
import { QuestionSidebar } from './components/question/question-sidebar';
import { Permission } from '../../../../../../../../../backend/src/auth/permissions/permission';
import { PageName, TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubset } from '@/api/queries/useGetSubset';
import useGetUser from '@/api/queries/useGetUser';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/new/components/shadcn/empty';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';
import { useMatterProcessingStore } from '@/new/stores/matter-processing-store';

export function Kim() {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;

  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: { label: 'Kim', path: 'kim' },
  });

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb items={breadcrumbItems} />
      <KimContent matterId={matterId} subsetId={subsetId} />
    </div>
  );
}

function KimContent({ matterId, subsetId }: { matterId: string; subsetId: string }) {
  const [isManualAnswerModalOpen, setIsManualAnswerModalOpen] = useState(false);
  const [isDeprecationDialogOpen, setIsDeprecationDialogOpen] = useState(false);

  const navigate = useNavigate();

  const { data: user } = useGetUser();

  useEffect(() => {
    trackPageView(PageName.Kim);
  }, []);

  const { hasPermission } = useHasPermission();
  const canCreateThread = hasPermission(Permission.ThreadCreate);
  const { getProcessingState } = useMatterProcessingStore();

  if (getProcessingState(matterId)?.hasCompletedRuns === false) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icon className="lucide--bot" />
          </EmptyMedia>
          <EmptyTitle>Kim Unavailable</EmptyTitle>
          <EmptyDescription>Kim is unavailable while documents are still processing.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="relative flex h-full w-full flex-1 items-center overflow-hidden">
      <QuestionSidebar taggedDocuments={[]} />

      {canCreateThread && user?.organisation?.isLabellingOrg && (
        <div className="absolute top-4 right-4 flex gap-2">
          <Button
            onClick={() => setIsManualAnswerModalOpen(true)}
            className="flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100"
          >
            <span>+ Create Manual Answer</span>
          </Button>
        </div>
      )}

      <div className="flex w-full flex-col items-center justify-center">
        <Empty>
          <EmptyHeader className="max-w-2xl">
            <EmptyTitle>This version of Kim is now read-only</EmptyTitle>
            <EmptyDescription>
              New questions can no longer be asked here. Your previous questions and answers are still available from
              the sidebar.
            </EmptyDescription>
          </EmptyHeader>

          <EmptyContent>
            <Button asChild variant="primary">
              <Link
                to={`/matters/${matterId}/subsets/${subsetId}/new-kim`}
                onClick={() => {
                  trackCustomEvent(TrackingEventsV2.KimEnableNewExperience, {
                    matterId,
                    subsetId,
                    from: 'Old Kim read-only page',
                  });
                  localStorage.setItem('isUsingNewKim', 'true');
                }}
              >
                <Icon className="lucide--sparkle" />
                <span>Go to the new Kim experience</span>
                <Icon className="lucide--arrow-right" />
              </Link>
            </Button>

            <button
              onClick={() => setIsDeprecationDialogOpen(true)}
              className="text-xs text-gray-500 underline underline-offset-4 duration-150 hover:text-gray-700"
            >
              Learn more about this change
            </button>
          </EmptyContent>
        </Empty>
      </div>

      <KimDeprecationDialog
        open={isDeprecationDialogOpen}
        onOpenChange={setIsDeprecationDialogOpen}
        subsetId={subsetId}
      />

      {/* Manual Answer Modal */}
      <ManualAnswerModal
        isOpen={isManualAnswerModalOpen}
        onClose={() => setIsManualAnswerModalOpen(false)}
        onComplete={(threadId) => {
          setIsManualAnswerModalOpen(false);
          navigate(`/matters/${matterId}/subsets/${subsetId}/kim/${threadId}`);
        }}
      />
    </div>
  );
}
